import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FaRobot } from "react-icons/fa";
import config from "../config";
import WorkerSidebarShell from "./WorkerSidebarShell";

const ActivatedPatients = () => {
  const navigate = useNavigate();
  const workerID = localStorage.getItem("workerID");
  const token = localStorage.getItem("token");

  const [patients, setPatients] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(true);
  const [error, setError] = useState(null);
  const [expandedPatientID, setExpandedPatientID] = useState(null);
  const authHeaders = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token]
  );

  const fetchPatients = useCallback(async () => {
    if (!workerID || !token) return;

    try {
      const res = await axios.get(`${config.API_BASE_URL}/worker/activated-patients`, {
        params: { workerID },
        headers: { Authorization: `Bearer ${token}` }
      });

      setPatients(res.data.patients || []);
      setError(null);
    } catch (err) {
      if (err.response?.status === 401) {
        setError("Session expired. Please log in again.");
      } else {
        setError("Failed to load activated residents.");
      }
    }
  }, [workerID, token]);

  const fetchAgents = useCallback(async () => {
    if (!token) {
      setLoadingAgents(false);
      return;
    }

    try {
      const res = await axios.get(
        `${config.API_BASE_URL}${config.WORKER_AGENTS.LIST}`,
        { headers: authHeaders }
      );
      setAgents(Array.isArray(res.data?.agents) ? res.data.agents : []);
    } catch {
      setAgents([]);
    } finally {
      setLoadingAgents(false);
    }
  }, [authHeaders, token]);

  useEffect(() => {
    fetchPatients();
    const interval = setInterval(fetchPatients, 15000);
    return () => clearInterval(interval);
  }, [fetchPatients]);

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  const displayGender = (genderValue) => {
    if (!genderValue) return "Not set";
    const value = String(genderValue).trim();
    if (value.toUpperCase() === "M") return "Male";
    if (value.toUpperCase() === "F") return "Female";
    return value;
  };

  const displayPhone = (patient) =>
    patient?.whatsapp ||
    patient?.phone ||
    patient?.phoneNumber ||
    "Not set";

  const openWorkerAgentWorkspace = (patient, preferredSlug = "") => {
    const patientId = String(patient?.patientID || "").trim();
    const patientName = `${patient?.firstName || ""} ${patient?.lastName || ""}`.trim();
    const params = new URLSearchParams({
      patient_id: patientId,
      patient_name: patientName
    });

    if (preferredSlug) {
      params.set("agent_slug", preferredSlug);
    }

    navigate(`/worker-agent-chat?${params.toString()}`, {
      state: {
        patientId,
        patientName,
        selectedAgentSlug: preferredSlug || ""
      }
    });
  };

  const openWorkerElderProfile = (patient, preferredSlug = "") => {
    const patientId = String(patient?.patientID || "").trim();
    const patientName = `${patient?.firstName || ""} ${patient?.lastName || ""}`.trim();
    const params = new URLSearchParams();

    if (patientName) {
      params.set("patient_name", patientName);
    }
    if (preferredSlug) {
      params.set("agent_slug", preferredSlug);
    }

    navigate(
      `/worker/patients/${encodeURIComponent(patientId)}${
        params.toString() ? `?${params.toString()}` : ""
      }`,
      {
        state: {
          patientId,
          patientName,
          selectedAgentSlug: preferredSlug || ""
        }
      }
    );
  };

  return (
    <WorkerSidebarShell activeItem="patients">
      <div style={card}>
        <h2 style={title}>Residents Who Activated You</h2>
        <p style={subtitle}>
          Select a resident card to expand details.
        </p>

        {error && <p style={errorText}>{error}</p>}

        {!error && patients.length === 0 && (
          <p style={emptyText}>No active residents yet.</p>
        )}

        {patients.map((p) => (
          <div key={p.patientID} style={patientCard}>
            <button
              type="button"
              style={nameButton}
              onClick={() =>
                setExpandedPatientID((prev) => (prev === p.patientID ? null : p.patientID))
              }
            >
              {p.firstName} {p.lastName}
            </button>

            {expandedPatientID === p.patientID ? (
              <div style={detailsStyle}>
                <p><strong>Language:</strong> {p.spokenLanguage || "Not set"}</p>
                <p><strong>Gender:</strong> {displayGender(p.gender)}</p>
                <p><strong>Location:</strong> {p.city || "-"}, {p.country || "-"}</p>
                <p><strong>WhatsApp:</strong> {displayPhone(p)}</p>

                <div style={agentsSection}>
                  <div style={agentsSectionHeader}>
                    <strong>Available Agents</strong>
                    <span style={agentCountPill}>
                      <FaRobot />
                      {loadingAgents ? "Loading..." : `${agents.length} ready`}
                    </span>
                  </div>

                  {!loadingAgents && agents.length === 0 ? (
                    <div style={agentEmptyState}>
                      <p style={agentEmptyText}>
                        You have not added any worker agents yet.
                      </p>
                      <button
                        type="button"
                        style={manageAgentsButton}
                        onClick={() => navigate("/worker-agents")}
                      >
                        Add Agents
                      </button>
                    </div>
                  ) : null}

                  {!loadingAgents && agents.length > 0 ? (
                    <>
                      <div style={agentChipRow}>
                        {agents.map((agent) => (
                          <button
                            key={agent.slug}
                            type="button"
                            style={agentChip}
                            onClick={() => openWorkerElderProfile(p, agent.slug)}
                          >
                            <FaRobot />
                            {agent.name}
                          </button>
                        ))}
                      </div>
                      <div style={actionRow}>
                        <button
                          type="button"
                          style={secondaryLaunchButton}
                          onClick={() => openWorkerElderProfile(p)}
                        >
                          Open Care Profile
                        </button>

                        <button
                          type="button"
                          style={launchButton}
                          onClick={() => openWorkerAgentWorkspace(p)}
                        >
                          Open Agent Workspace
                        </button>
                      </div>
                    </>
                  ) : null}
                </div>
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </WorkerSidebarShell>
  );
};

export default ActivatedPatients;

const card = {
  background: "#fff",
  border: "1px solid #e3edf5",
  borderRadius: "12px",
  padding: "24px",
  boxShadow: "0 4px 16px rgba(0,0,0,0.05)",
  width: "100%",
  maxWidth: "860px",
  margin: "0 auto"
};

const title = {
  margin: "0 0 6px",
  color: "#0E5580",
  fontSize: "28px"
};

const subtitle = {
  marginTop: 0,
  color: "#4f6475",
  marginBottom: "18px"
};

const patientCard = {
  border: "1px solid #dce7f1",
  padding: "14px",
  marginBottom: "12px",
  borderRadius: "10px",
  background: "#f9fcff"
};

const nameButton = {
  border: "none",
  background: "transparent",
  padding: 0,
  cursor: "pointer",
  color: "#0E5580",
  fontSize: "18px",
  fontWeight: 600
};

const detailsStyle = {
  marginTop: "10px",
  paddingTop: "10px",
  borderTop: "1px solid #e6eef5",
  color: "#234159",
  lineHeight: 1.6,
  display: "grid",
  gap: "6px"
};

const agentsSection = {
  marginTop: "8px",
  borderTop: "1px solid #dde9f2",
  paddingTop: "12px",
  display: "grid",
  gap: "12px"
};

const agentsSectionHeader = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "10px",
  flexWrap: "wrap"
};

const agentCountPill = {
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  borderRadius: "999px",
  background: "#edf7ff",
  color: "#0E5580",
  padding: "6px 10px",
  fontSize: "12px",
  fontWeight: 700
};

const agentEmptyState = {
  border: "1px dashed #c3d7e6",
  borderRadius: "10px",
  padding: "12px",
  background: "#f8fbfd",
  display: "grid",
  gap: "10px"
};

const agentEmptyText = {
  margin: 0,
  color: "#4f6475"
};

const manageAgentsButton = {
  justifySelf: "flex-start",
  border: "1px solid #c7dae8",
  background: "#fff",
  color: "#0E5580",
  borderRadius: "8px",
  padding: "8px 12px",
  cursor: "pointer",
  fontWeight: 700
};

const agentChipRow = {
  display: "flex",
  flexWrap: "wrap",
  gap: "8px"
};

const actionRow = {
  display: "flex",
  flexWrap: "wrap",
  gap: "10px"
};

const agentChip = {
  border: "1px solid #cce1ef",
  background: "#f1f9ff",
  color: "#0E5580",
  borderRadius: "999px",
  padding: "8px 12px",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  fontWeight: 600
};

const launchButton = {
  border: "none",
  background: "#0E5580",
  color: "#fff",
  borderRadius: "10px",
  padding: "10px 14px",
  cursor: "pointer",
  fontWeight: 700
};

const secondaryLaunchButton = {
  border: "1px solid #c7dae8",
  background: "#fff",
  color: "#0E5580",
  borderRadius: "10px",
  padding: "10px 14px",
  cursor: "pointer",
  fontWeight: 700
};

const errorText = {
  color: "#b42318",
  background: "#fef3f2",
  border: "1px solid #fecdd3",
  borderRadius: "8px",
  padding: "10px"
};

const emptyText = {
  color: "#4f6475",
  fontStyle: "italic"
};
